/**
 * Shared research contracts used by the Office add-ins. This is the single
 * source of truth for:
 *
 *  - the public brand context (Finster vs FactSet AI naming / labels and the
 *    brand-specific public tool name `finster_query` vs `factset_ai_query`),
 *  - full and focused research tool names and the `{ query }` input schema,
 *  - the streaming and buffered HTTP calls to the internal data agent.
 *
 * Each surface keeps its own natural-language tool *description* (the deck
 * builder and the spreadsheet agent want different guidance) and its own
 * delivery channel: PowerPoint additionally streams sub-events to its chat
 * timeline (`powerpoint/api/finster/queries.ts::queryFinsterStream`), while the
 * Excel executor loop consumes a single buffered tool result. The request
 * payload, data-source contract, and branding are identical via this module.
 */

import { buildApiUrl } from "../../config";
import { withCredentials } from "../../apiClient";
import { fetchWithRetry } from "../../utils/http";
import { checkAuthStatus } from "../../utils/authStatus";
import { parseErrorResponse } from "../../utils/apiError";
import { isAbortError } from "../../utils/abortError";
import { withLangfuseMetadataHeader } from "../../services/langfuseTrace";
import { isFinsterChatSessionId } from "../../utils/sessionId";
import { appendUserFilesDataSource } from "../../researchSelection";
import {
  AGENT_FLAG,
  FINSTER_QUERY_MODE_AGENT_FLAGS,
  type FinsterQueryDataSource,
  type FinsterQueryMode,
  type PublicDataSourceBrandName,
} from "./constants";

// ---------------------------------------------------------------------------
// Brand context
// ---------------------------------------------------------------------------

/** User-visible brand of the research agent. */
export type PublicBrandName = PublicDataSourceBrandName;

/** Stable caller attribution accepted by the add-in BFF. */
export type FinsterClientOrigin = "powerpoint_addin" | "excel_addin";

/** Backend limits from data-feed's PowerPointClientContext model. */
export const FINSTER_CONTEXT_IDENTIFIER_MAX_LENGTH = 256;
export const FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH = 512;

/** Host metadata accepted alongside a matching office add-in client_origin. */
export interface PowerPointClientContext {
  presentation_id?: string;
  presentation_name?: string;
  slide_id?: string;
  slide_number?: number;
  slide_title?: string;
}

export interface ExcelClientContext {
  workbook_id?: string;
  workbook_name?: string;
  sheet_id?: string;
  sheet_name?: string;
  sheet_number?: number;
}

export type OfficeClientContext = PowerPointClientContext | ExcelClientContext;

/** Brand-specific public name of the research tool exposed to the model. */
export type PublicResearchToolName = "finster_query" | "factset_ai_query";

export const FINSTER_RESEARCH_TOOL_NAME: PublicResearchToolName = "finster_query";
export const FACTSET_RESEARCH_TOOL_NAME: PublicResearchToolName = "factset_ai_query";
export const FILINGS_QUERY_TOOL_NAME = "filings_query" as const;
export type FilingsQueryToolName = typeof FILINGS_QUERY_TOOL_NAME;

/**
 * Brand-dependent display strings for the research tool. Carries every label
 * either surface needs; surfaces use the subset relevant to them (Excel uses
 * the tool name + status / result labels; PowerPoint additionally uses the
 * citation-markup and task labels).
 */
export interface FinsterBrandContext {
  publicBrandName: PublicBrandName;
  researchToolName: PublicResearchToolName;
  researchToolDescriptionBrand: PublicBrandName;
  researchToolDataLabel: string;
  researchStatusLabel: string;
  researchResultLabel: string;
  taskContextHeaderBrand: "FINSTER" | "FACTSET AI";
  taskRunOutputLabel: string;
  citationMarkupLabel: string;
}

const FINSTER_BRAND_CONTEXT: FinsterBrandContext = {
  publicBrandName: "Finster",
  researchToolName: FINSTER_RESEARCH_TOOL_NAME,
  researchToolDescriptionBrand: "Finster",
  researchToolDataLabel: "Finster Data",
  researchStatusLabel: "Querying Finster",
  researchResultLabel: "Finster Data",
  taskContextHeaderBrand: "FINSTER",
  taskRunOutputLabel: "Finster task run output",
  citationMarkupLabel: "Finster citation markup",
};

const FACTSET_BRAND_CONTEXT: FinsterBrandContext = {
  publicBrandName: "FactSet AI",
  researchToolName: FACTSET_RESEARCH_TOOL_NAME,
  researchToolDescriptionBrand: "FactSet AI",
  researchToolDataLabel: "FactSet AI",
  researchStatusLabel: "Querying FactSet AI",
  researchResultLabel: "FactSet AI",
  taskContextHeaderBrand: "FACTSET AI",
  taskRunOutputLabel: "FactSet AI task run output",
  citationMarkupLabel: "FactSet AI citation markup",
};

/**
 * Resolve the brand context. Defaults to Finster; selects FactSet AI when the
 * surface is running inside the FactSet environment. An explicit
 * `researchToolName` override wins over the brand default (kept for parity with
 * the PowerPoint policy surface).
 */
export function resolveFinsterBrandContext(
  publicBrandName?: PublicBrandName | null,
  researchToolName?: PublicResearchToolName | null
): FinsterBrandContext {
  const base = publicBrandName === "FactSet AI" ? FACTSET_BRAND_CONTEXT : FINSTER_BRAND_CONTEXT;
  return {
    ...base,
    researchToolName: researchToolName ?? base.researchToolName,
  };
}

/** Type guard: is `tool` one of the brand-specific research tool names? */
export function isResearchToolName(tool: string): tool is PublicResearchToolName {
  return tool === FINSTER_RESEARCH_TOOL_NAME || tool === FACTSET_RESEARCH_TOOL_NAME;
}

/** Type guard for the focused filings/IR research route. */
export function isFilingsQueryToolName(tool: string): tool is FilingsQueryToolName {
  return tool === FILINGS_QUERY_TOOL_NAME;
}

/** Type guard covering the full and focused research routes. */
export function isAnyResearchToolName(
  tool: string
): tool is PublicResearchToolName | FilingsQueryToolName {
  return isResearchToolName(tool) || isFilingsQueryToolName(tool);
}

// ---------------------------------------------------------------------------
// Model-facing input schema (shared)
// ---------------------------------------------------------------------------

/**
 * Canonical input schema for the research tool. Both surfaces expose the same
 * single required `query` string; only the surrounding description differs.
 */
export const FINSTER_QUERY_INPUT_SCHEMA = {
  type: "object",
  properties: {
    query: {
      type: "string",
      description:
        "Natural-language research question (e.g. \"Summarize NVIDIA's most " +
        'recent earnings call", "Summarize a company\'s latest SEC filing", ' +
        '"Who are Datadog\'s main competitors?").',
    },
  },
  required: ["query"],
  additionalProperties: false,
} as const;

// ---------------------------------------------------------------------------
// Query execution core
// ---------------------------------------------------------------------------

/**
 * Tool-scoped timeout for a single research call. Meta-agent research is
 * multi-hop and may coordinate several concurrent subqueries.
 */
export const FINSTER_QUERY_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_QUERY_USER_FILE_IDS = 1000;
const MAX_QUERY_USER_FILE_ID_LENGTH = 1024;
const INCLUDE_SESSION_HISTORY = false;

const SESSION_ID_KEY = "session_id";
const SESSION_NAME_KEY = "session_name";

/**
 * Older data-feed builds may 422 on unknown/unsupported attribution fields.
 * Treat those as non-fatal so research still works when attribution isn't
 * deployed yet — chat grouping just won't apply for that query.
 */
export function isClientAttributionRejection(status: number, errorText: string): boolean {
  if (status !== 422) return false;
  const normalized = errorText.toLowerCase();
  return normalized.includes("client_origin") || normalized.includes("client_context");
}

/**
 * Reads and trims a string field from an API record.
 */
function getTrimmedSessionString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return undefined;
}

/**
 * Pull canonical Finster session metadata out of a query response or SSE frame.
 * We intentionally only read the snake_case API-contract keys (`session_id` /
 * `session_name`) so Excel and PowerPoint share the same extraction logic.
 */
export function extractFinsterSession(source: unknown): { id?: string; name?: string } {
  if (!source || typeof source !== "object") return {};
  const record = source as Record<string, unknown>;
  const id = getTrimmedSessionString(record, SESSION_ID_KEY);
  const name = getTrimmedSessionString(record, SESSION_NAME_KEY);
  return { ...(id ? { id } : {}), ...(name ? { name } : {}) };
}

export interface FinsterQueryExecResult {
  success: boolean;
  answer?: string;
  citations?: unknown;
  /** Citations projected on the final meta-agent response. */
  rootCitations?: unknown[];
  /** Deduplicated citations retained from delegated agent responses. */
  delegatedCitations?: unknown[];
  error?: string;
  /** Upstream Finster/FactSet session id (`session_id`), for trace correlation. */
  sessionId?: string;
  /** Upstream Finster/FactSet session name (`session_name`), for trace correlation. */
  sessionName?: string;
}

export interface ExecuteFinsterQueryParams {
  query: string;
  /** Office host that owns this query for downstream trace attribution. */
  deliveryChannel: "excel" | "powerpoint";
  /**
   * Selects the upstream research orchestrator. Native Excel opts into the
   * meta-agent; omitted callers retain the existing data-agent contract.
   */
  researchTransport?: "meta_agent";
  /**
   * Chat-tab id (ULID) shared by every turn in this tab. The add-in server
   * forwards it as the upstream `session_id`, so follow-up prompts append to
   * one Finster Chat session instead of each minting a new one.
   */
  chatSessionId?: string;
  /** Host surface making the request, forwarded for server-side attribution. */
  clientOrigin?: FinsterClientOrigin;
  /** Optional host workbook/presentation metadata for session attribution. */
  clientContext?: OfficeClientContext;
  /**
   * Data sources to request. `undefined` omits the constraint even when files
   * are attached; `[]` is an explicit empty scope (files-only once IDs are
   * present). Callers remain responsible for entitlement filtering.
   */
  dataSources?: readonly FinsterQueryDataSource[];
  /** User-uploaded file ids to scope retrieval to. */
  userFileIds?: readonly string[];
  /** Exact resolved Skill guidance for the nested model call. Never merge into `query`. */
  selectedSkillsGuidance?: string;
  /** User-visible brand sent to the agent. Defaults to "Finster". */
  publicBrandName?: PublicBrandName;
  /** Agent mode. Defaults to "thinking" (multi-hop). */
  mode?: FinsterQueryMode;
  /** Cancellation signal from the surrounding tool loop. */
  signal?: AbortSignal;
  /** Tool-scoped timeout. Defaults to {@link FINSTER_QUERY_TIMEOUT_MS}. */
  timeoutMs?: number;
  /** Office turn metadata used only by the trusted add-in server proxy. */
  langfuseMetadata?: Record<string, unknown>;
}

function validateSelectedSkillsGuidance(guidance: unknown): string | undefined {
  if (guidance === undefined) return undefined;
  if (typeof guidance !== "string" || !guidance.trim()) {
    return "Selected Skill guidance must be a non-empty string.";
  }
  return undefined;
}

/** Omitted sources stay unrestricted even when files are attached. */
function resolveRequestDataSources(
  dataSources: readonly FinsterQueryDataSource[] | undefined,
  hasUserFiles: boolean
): string[] | undefined {
  if (dataSources === undefined) return undefined;
  return appendUserFilesDataSource(dataSources, hasUserFiles);
}

/**
 * Normalized sub-events emitted while the Finster/FactSet agent stream is
 * running. Consumers use these to render nested reasoning, tool calls, answer
 * deltas, and the final cited response without depending on raw SSE frame names.
 */
export type FinsterStreamSubEvent =
  | { type: "status"; status: string }
  | { type: "session"; sessionName?: string }
  | { type: "reasoning"; chunk: string; hopIndex: number }
  | {
      type: "tool";
      toolName: string;
      status: "calling" | "completed" | "failed";
      callId?: string;
      hopIndex: number;
      description?: string;
      sectionTitle?: string;
    }
  | { type: "answerDelta"; chunk: string }
  | { type: "final"; answer: string; citations?: unknown[] }
  | {
      type: "delegationStarted";
      delegationId: string;
      title: string;
      query: string;
    }
  | {
      type: "delegationSession";
      delegationId: string;
      childSessionId: string;
      query: string;
    }
  | {
      type: "delegationStatus";
      delegationId: string;
      childSessionId?: string;
      status: string;
    }
  | {
      type: "delegationReasoning";
      delegationId: string;
      childSessionId?: string;
      chunk: string;
      hopIndex: number;
    }
  | {
      type: "delegationTool";
      delegationId: string;
      childSessionId?: string;
      toolName: string;
      status: "calling" | "completed" | "failed";
      callId?: string;
      hopIndex: number;
      description?: string;
      sectionTitle?: string;
    }
  | {
      type: "delegationAnswerDelta";
      delegationId: string;
      childSessionId?: string;
      chunk: string;
    }
  | {
      type: "delegationFinal";
      delegationId: string;
      childSessionId?: string;
      queryId?: string;
      answer: string;
      citations?: unknown[];
    }
  | {
      type: "delegationCompleted";
      delegationId: string;
      status: "completed" | "failed";
      message?: string;
    };

/**
 * Executes the existing research tool through the buffered JSON endpoint.
 * This is used only when one executor turn contains multiple independent
 * research calls; standalone calls keep the richer streaming timeline.
 */
export async function executeFinsterQueryBuffered(
  params: ExecuteFinsterQueryParams
): Promise<FinsterQueryExecResult> {
  const {
    query,
    deliveryChannel,
    researchTransport,
    chatSessionId,
    clientOrigin,
    clientContext,
    dataSources,
    userFileIds = [],
    selectedSkillsGuidance,
    publicBrandName = "Finster",
    mode = "thinking",
    signal,
    timeoutMs = FINSTER_QUERY_TIMEOUT_MS,
    langfuseMetadata,
  } = params;
  const agentFlag = FINSTER_QUERY_MODE_AGENT_FLAGS[mode] ?? AGENT_FLAG;
  const selectedSkillsGuidanceError = validateSelectedSkillsGuidance(selectedSkillsGuidance);
  if (selectedSkillsGuidanceError) {
    return { success: false, error: selectedSkillsGuidanceError };
  }

  if (userFileIds.length > MAX_QUERY_USER_FILE_IDS) {
    return {
      success: false,
      error: `Your Files supports at most ${MAX_QUERY_USER_FILE_IDS} selected files per query.`,
    };
  }
  if (
    userFileIds.some(
      (fileId) =>
        typeof fileId !== "string" ||
        !fileId.trim() ||
        fileId.trim().length > MAX_QUERY_USER_FILE_ID_LENGTH
    )
  ) {
    return {
      success: false,
      error: "The selected Your Files scope contains an invalid file identifier.",
    };
  }

  const normalizedUserFileIds = Array.from(new Set(userFileIds.map((fileId) => fileId.trim())));
  const requestDataSources = resolveRequestDataSources(
    dataSources,
    normalizedUserFileIds.length > 0
  );

  // `session_id` stays outside the attribution block: the 422 retry below drops
  // client_origin/client_context, and session continuity must survive that.
  const buildBody = (includeAttribution: boolean) =>
    JSON.stringify({
      query,
      ...(researchTransport ? { research_transport: researchTransport } : {}),
      ...(selectedSkillsGuidance !== undefined
        ? { selected_skills_guidance: selectedSkillsGuidance }
        : {}),
      ...(isFinsterChatSessionId(chatSessionId) ? { session_id: chatSessionId.trim() } : {}),
      include_session_history: INCLUDE_SESSION_HISTORY,
      ...(includeAttribution && clientOrigin
        ? {
            client_origin: clientOrigin,
            ...(hasClientContextValues(clientContext) ? { client_context: clientContext } : {}),
          }
        : {}),
      ...(requestDataSources !== undefined ? { data_sources: requestDataSources } : {}),
      ...(normalizedUserFileIds.length > 0 ? { user_file_ids: normalizedUserFileIds } : {}),
      agent_flag: agentFlag,
      output_mode: "markdown",
      publicBrandName,
    });
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onCallerAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) timeoutController.abort();
    else signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  try {
    const fetchBuffered = (includeAttribution: boolean) =>
      fetchWithRetry(
        buildApiUrl("/api/finster/query"),
        withLangfuseMetadataHeader(
          withCredentials({
            method: "POST",
            headers: {
              "X-Finster-Office-Host": deliveryChannel,
            },
            body: buildBody(includeAttribution),
            signal: timeoutController.signal,
          }),
          langfuseMetadata
        ),
        1,
        "FINSTER_QUERY",
        timeoutMs
      );

    let response = await fetchBuffered(true);

    checkAuthStatus(response);
    if (!response.ok) {
      const error = await parseErrorResponse(response);
      if (isClientAttributionRejection(response.status, error)) {
        console.warn(
          "[FinsterQuery] Attribution rejected by upstream; retrying without client_origin/client_context"
        );
        response = await fetchBuffered(false);
        checkAuthStatus(response);
        if (!response.ok) {
          return { success: false, error: await parseErrorResponse(response) };
        }
      } else {
        return { success: false, error };
      }
    }

    const data = (await response.json()) as Record<string, unknown>;
    const answer =
      typeof data.output === "string"
        ? data.output
        : typeof data.answer === "string"
          ? data.answer
          : "";
    if (!answer.trim()) {
      return { success: false, error: "Finster query returned an empty response." };
    }
    const session = extractFinsterSession(data);
    return {
      success: true,
      answer,
      citations: Array.isArray(data.citations) ? data.citations : undefined,
      rootCitations: Array.isArray(data.final_citations)
        ? data.final_citations
        : undefined,
      delegatedCitations: Array.isArray(data.delegated_citations)
        ? data.delegated_citations
        : undefined,
      ...(session.id ? { sessionId: session.id } : {}),
      ...(session.name ? { sessionName: session.name } : {}),
    };
  } catch (err) {
    if (signal?.aborted) throw err;
    if (isAbortError(err)) {
      return {
        success: false,
        error: `finster_query timed out after ${Math.round(timeoutMs / 1000)}s.`,
      };
    }
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
}

/**
 * Maps backend tool status strings into the UI's three supported states.
 */
function normalizeFinsterToolStatus(raw: unknown): "calling" | "completed" | "failed" {
  const value = typeof raw === "string" ? raw.toLowerCase() : "";
  if (value === "completed") return "completed";
  if (value === "failed" || value === "error") return "failed";
  return "calling";
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function compactDelegationTitle(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= 72) return normalized;
  return `${normalized.slice(0, 69).trimEnd()}...`;
}

function mergeUniqueCitations(
  current: readonly unknown[],
  incoming: readonly unknown[] | undefined
): unknown[] {
  if (!incoming?.length) return [...current];
  const merged = [...current];
  const seen = new Set(
    current.map((citation) => {
      const record = asRecord(citation);
      return (
        nonEmptyString(record?.id) ??
        nonEmptyString(record?.citation_id) ??
        nonEmptyString(record?.citationId) ??
        JSON.stringify(citation)
      );
    })
  );
  for (const citation of incoming) {
    const record = asRecord(citation);
    const key =
      nonEmptyString(record?.id) ??
      nonEmptyString(record?.citation_id) ??
      nonEmptyString(record?.citationId) ??
      JSON.stringify(citation);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(citation);
  }
  return merged;
}

function hasClientContextValues(
  clientContext: OfficeClientContext | undefined
): clientContext is OfficeClientContext {
  if (!clientContext) return false;
  for (const key in clientContext) {
    const value = clientContext[key as keyof OfficeClientContext];
    if (value !== undefined && value !== null && value !== "") {
      return true;
    }
  }
  return false;
}

/**
 * Executes the streaming Finster/FactSet research endpoint and buffers the final
 * model-facing result while forwarding normalized sub-events to the caller.
 */
export async function executeFinsterQueryStream(
  params: ExecuteFinsterQueryParams & {
    onSubEvent?: (event: FinsterStreamSubEvent) => void;
  }
): Promise<FinsterQueryExecResult> {
  if (typeof ReadableStream === "undefined") {
    return { success: false, error: "Finster streaming is not available in this environment." };
  }

  const {
    query,
    deliveryChannel,
    researchTransport,
    chatSessionId,
    clientOrigin,
    clientContext,
    dataSources,
    userFileIds = [],
    selectedSkillsGuidance,
    publicBrandName = "Finster",
    mode = "thinking",
    signal,
    timeoutMs = FINSTER_QUERY_TIMEOUT_MS,
    langfuseMetadata,
    onSubEvent = () => {},
  } = params;
  const agentFlag = FINSTER_QUERY_MODE_AGENT_FLAGS[mode] ?? AGENT_FLAG;
  const selectedSkillsGuidanceError = validateSelectedSkillsGuidance(selectedSkillsGuidance);
  if (selectedSkillsGuidanceError) {
    return { success: false, error: selectedSkillsGuidanceError };
  }
  if (userFileIds.length > MAX_QUERY_USER_FILE_IDS) {
    return {
      success: false,
      error: `Your Files supports at most ${MAX_QUERY_USER_FILE_IDS} selected files per query.`,
    };
  }
  if (
    userFileIds.some(
      (fileId) =>
        typeof fileId !== "string" ||
        !fileId.trim() ||
        fileId.trim().length > MAX_QUERY_USER_FILE_ID_LENGTH
    )
  ) {
    return {
      success: false,
      error: "The selected Your Files scope contains an invalid file identifier.",
    };
  }
  const normalizedUserFileIds = Array.from(new Set(userFileIds.map((fileId) => fileId.trim())));
  const requestDataSources = resolveRequestDataSources(
    dataSources,
    normalizedUserFileIds.length > 0
  );

  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onCallerAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) timeoutController.abort();
    else signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  try {
    // `session_id` stays outside the attribution block: the 422 retry below
    // drops client_origin/client_context, and continuity must survive that.
    const buildBody = (includeAttribution: boolean) =>
      JSON.stringify({
        query,
        ...(researchTransport ? { research_transport: researchTransport } : {}),
        ...(selectedSkillsGuidance !== undefined
          ? { selected_skills_guidance: selectedSkillsGuidance }
          : {}),
        ...(isFinsterChatSessionId(chatSessionId) ? { session_id: chatSessionId.trim() } : {}),
        include_session_history: INCLUDE_SESSION_HISTORY,
        ...(includeAttribution && clientOrigin
          ? {
              client_origin: clientOrigin,
              ...(hasClientContextValues(clientContext) ? { client_context: clientContext } : {}),
            }
          : {}),
        ...(requestDataSources !== undefined ? { data_sources: requestDataSources } : {}),
        ...(normalizedUserFileIds.length > 0 ? { user_file_ids: normalizedUserFileIds } : {}),
        agent_flag: agentFlag,
        output_mode: "markdown",
        publicBrandName,
      });

    const fetchStream = (includeAttribution: boolean) =>
      fetchWithRetry(
        buildApiUrl("/api/finster/query/stream"),
        withLangfuseMetadataHeader(
          withCredentials({
            method: "POST",
            headers: {
              "X-Finster-Office-Host": deliveryChannel,
            },
            body: buildBody(includeAttribution),
            signal: timeoutController.signal,
          }),
          langfuseMetadata
        ),
        1,
        "FINSTER_QUERY_STREAM",
        timeoutMs
      );

    let response = await fetchStream(true);

    checkAuthStatus(response);
    if (!response.ok) {
      const error = await parseErrorResponse(response);
      if (isClientAttributionRejection(response.status, error)) {
        console.warn(
          "[FinsterQuery] Attribution rejected by upstream; retrying without client_origin/client_context"
        );
        response = await fetchStream(false);
        checkAuthStatus(response);
        if (!response.ok) {
          return { success: false, error: await parseErrorResponse(response) };
        }
      } else {
        return { success: false, error };
      }
    }
    if (!response.body) {
      return { success: false, error: "Finster stream returned no response body." };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    let currentEvent: string | null = null;
    let liveAnswer = "";
    let finalAnswer = "";
    let citations: unknown[] | undefined;
    let delegatedCitations: unknown[] = [];
    let sessionId: string | undefined;
    let sessionName: string | undefined;
    let streamError: string | undefined;
    const openChildSessions = new Set<string>();
    const settledChildSessions = new Set<string>();
    const pendingChildEvents = new Map<
      string,
      Array<{ type: string; data: Record<string, unknown> }>
    >();
    const pendingQueryTools = new Map<string, { query: string; summary?: string }>();
    const pendingChildResults = new Map<
      string,
      { summary?: string; status: "completed" | "failed"; message?: string }
    >();

    const handleExplorerEvent = (
      type: string,
      evt: Record<string, unknown>,
      delegation?: { id: string; childSessionId?: string }
    ) => {
      if (type === "status") {
        const status = String(evt.status ?? evt.text ?? "");
        if (delegation) {
          onSubEvent({
            type: "delegationStatus",
            delegationId: delegation.id,
            childSessionId: delegation.childSessionId,
            status,
          });
        } else {
          onSubEvent({ type: "status", status });
        }
      } else if (type === "session") {
        if (!delegation) {
          const found = extractFinsterSession(evt);
          if (!sessionId && found.id) sessionId = found.id;
          if (!sessionName && found.name) sessionName = found.name;
          onSubEvent({ type: "session", sessionName: found.name });
        }
      } else if (type === "reasoningChunk") {
        const chunk = String(evt.chunk ?? "");
        const hopIndex = Number(evt.hop_index ?? evt.iteration ?? 0);
        if (delegation) {
          onSubEvent({
            type: "delegationReasoning",
            delegationId: delegation.id,
            childSessionId: delegation.childSessionId,
            chunk,
            hopIndex,
          });
        } else {
          onSubEvent({ type: "reasoning", chunk, hopIndex });
        }
      } else if (type === "toolCallStatus") {
        const toolEvent = {
          toolName: String(evt.tool_name ?? ""),
          status: normalizeFinsterToolStatus(evt.status),
          callId: evt.call_id as string | undefined,
          hopIndex: Number(evt.hop_index ?? 0),
          description: evt.description as string | undefined,
          sectionTitle: evt.section_title as string | undefined,
        };
        if (delegation) {
          onSubEvent({
            type: "delegationTool",
            delegationId: delegation.id,
            childSessionId: delegation.childSessionId,
            ...toolEvent,
          });
        } else {
          onSubEvent({ type: "tool", ...toolEvent });
        }
      } else if (type === "llmChunk" || type === "textChunk") {
        const chunk = String(evt.chunk ?? "");
        if (!chunk) return;
        if (delegation) {
          onSubEvent({
            type: "delegationAnswerDelta",
            delegationId: delegation.id,
            childSessionId: delegation.childSessionId,
            chunk,
          });
        } else {
          liveAnswer += chunk;
          onSubEvent({ type: "answerDelta", chunk });
        }
      } else if (type === "agentResponse") {
        const agent = asRecord(evt.agent) ?? evt;
        const out = nonEmptyString(agent.output) ?? nonEmptyString(evt.output) ?? "";
        const eventCitations =
          (Array.isArray(agent.citations) ? agent.citations : undefined) ??
          (Array.isArray(evt.citations) ? evt.citations : undefined);
        if (delegation) {
          delegatedCitations = mergeUniqueCitations(delegatedCitations, eventCitations);
          settledChildSessions.add(delegation.id);
          onSubEvent({
            type: "delegationFinal",
            delegationId: delegation.id,
            childSessionId: delegation.childSessionId,
            queryId: nonEmptyString(evt.query_id) ?? nonEmptyString(agent.query_id),
            answer: out,
            citations: eventCitations,
          });
        } else if (out) {
          finalAnswer = out;
          citations = eventCitations ?? citations;
          const agentSession = extractFinsterSession(agent);
          const evtSession = extractFinsterSession(evt);
          if (!sessionId) sessionId = agentSession.id ?? evtSession.id;
          if (!sessionName) sessionName = agentSession.name ?? evtSession.name;
        }
      }
    };

    const routeChildEvent = (
      childSessionId: string,
      type: string,
      data: Record<string, unknown>
    ) => {
      handleExplorerEvent(type, data, {
        id: childSessionId,
        childSessionId,
      });
    };

    const applyChildResult = (
      childSessionId: string,
      query: string,
      result: { summary?: string; status: "completed" | "failed"; message?: string }
    ) => {
      if (result.summary) {
        onSubEvent({
          type: "delegationStarted",
          delegationId: childSessionId,
          title: compactDelegationTitle(result.summary),
          query,
        });
      }
      onSubEvent({
        type: "delegationCompleted",
        delegationId: childSessionId,
        status: result.status,
        message: result.message,
      });
      settledChildSessions.add(childSessionId);
    };

    const openChildSession = (childSessionId: string, query: string) => {
      if (openChildSessions.has(childSessionId)) return;
      openChildSessions.add(childSessionId);
      onSubEvent({
        type: "delegationStarted",
        delegationId: childSessionId,
        title: compactDelegationTitle(query || "Delegated research"),
        query,
      });
      for (const pending of pendingChildEvents.get(childSessionId) ?? []) {
        routeChildEvent(childSessionId, pending.type, pending.data);
      }
      pendingChildEvents.delete(childSessionId);
      const result = pendingChildResults.get(childSessionId);
      if (result) {
        applyChildResult(childSessionId, query, result);
        pendingChildResults.delete(childSessionId);
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split("\n");
      sseBuffer = lines.pop() || "";
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
          currentEvent = null;
          continue;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
          continue;
        }
        if (!line.startsWith("data:")) continue;
        const dataStr = line.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;
        let evt: Record<string, unknown>;
        try {
          evt = JSON.parse(dataStr) as Record<string, unknown>;
        } catch {
          continue;
        }
        const type = currentEvent || (evt.event as string) || (evt.type as string) || "";
        if (type === "meta_session") {
          sessionId = nonEmptyString(evt.meta_session_id) ?? sessionId;
          onSubEvent({ type: "session", sessionName: nonEmptyString(evt.meta_session_id) });
        } else if (type === "response") {
          const content = String(evt.content ?? "");
          if (content) {
            if (evt.partial === true) {
              liveAnswer += content;
              onSubEvent({ type: "answerDelta", chunk: content });
            } else {
              finalAnswer = content;
              if (Array.isArray(evt.citations)) citations = evt.citations;
            }
          }
        } else if (type === "tool_start") {
          const id = nonEmptyString(evt.id);
          const name = nonEmptyString(evt.name);
          const args = asRecord(evt.args);
          if (id && name === "run_query" && args) {
            pendingQueryTools.set(id, {
              query: nonEmptyString(args.query) ?? "",
              summary: nonEmptyString(args.summary),
            });
          }
        } else if (type === "query_session") {
          const childSessionId = nonEmptyString(evt.sessionId);
          if (childSessionId) {
            openChildSession(childSessionId, nonEmptyString(evt.query) ?? "");
          }
        } else if (type === "explorer_event") {
          const nestedType = nonEmptyString(evt.event) ?? "";
          const nested = asRecord(evt.data) ?? {};
          // Citation retention must not depend on timeline/session routing:
          // some child finals arrive before `query_session`, or without a
          // child session id.
          if (nestedType === "agentResponse") {
            const nestedAgent = asRecord(nested.agent) ?? nested;
            const childCitations =
              (Array.isArray(nestedAgent.citations)
                ? nestedAgent.citations
                : undefined) ??
              (Array.isArray(nested.citations) ? nested.citations : undefined);
            delegatedCitations = mergeUniqueCitations(
              delegatedCitations,
              childCitations,
            );
          }
          const childSessionId = nonEmptyString(evt.sessionId);
          if (!childSessionId) continue;
          if (!openChildSessions.has(childSessionId)) {
            const pending = pendingChildEvents.get(childSessionId) ?? [];
            pending.push({ type: nestedType, data: nested });
            pendingChildEvents.set(childSessionId, pending);
            continue;
          }
          routeChildEvent(childSessionId, nestedType, nested);
        } else if (type === "tool_result") {
          const result = asRecord(evt.result);
          const data = asRecord(result?.data);
          const childSessionId =
            nonEmptyString(data?.sessionId) ?? nonEmptyString(result?.sessionId);
          if (!childSessionId) continue;
          const tool = pendingQueryTools.get(nonEmptyString(evt.id) ?? "");
          const childResult = {
            summary: tool?.summary,
            status: result?.success === false ? ("failed" as const) : ("completed" as const),
            message: nonEmptyString(result?.message),
          };
          if (!openChildSessions.has(childSessionId)) {
            pendingChildResults.set(childSessionId, childResult);
            continue;
          }
          applyChildResult(childSessionId, tool?.query ?? "", childResult);
        } else if (type === "error" && researchTransport === "meta_agent") {
          streamError = nonEmptyString(evt.message) ?? "Meta-agent research failed.";
        } else if (
          type !== "done" &&
          type !== "explorer_summary" &&
          type !== "project_recommendation"
        ) {
          handleExplorerEvent(type, evt);
        }
      }
    }

    const answer = finalAnswer || liveAnswer;
    for (const childSessionId of openChildSessions) {
      if (settledChildSessions.has(childSessionId)) continue;
      onSubEvent({
        type: "delegationCompleted",
        delegationId: childSessionId,
        status: "failed",
        message: "Parent research stream ended before this delegated query completed.",
      });
    }
    if (streamError) {
      return { success: false, error: streamError };
    }
    if (!answer) {
      return {
        success: false,
        error: "Finster stream returned an empty response.",
      };
    }
    const rootCitations = citations;
    if (!citations?.length && delegatedCitations.length > 0) {
      citations = delegatedCitations;
    }
    onSubEvent({ type: "final", answer, citations });
    return {
      success: true,
      answer,
      citations,
      rootCitations: rootCitations ?? [],
      ...(delegatedCitations.length > 0 ? { delegatedCitations } : {}),
      ...(sessionId ? { sessionId } : {}),
      ...(sessionName ? { sessionName } : {}),
    };
  } catch (err) {
    if (signal?.aborted) {
      throw err;
    }
    if (isAbortError(err)) {
      return {
        success: false,
        error: `finster_query timed out after ${Math.round(timeoutMs / 1000)}s.`,
      };
    }
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onCallerAbort);
  }
}
